import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag, GripVertical } from 'lucide-react';

const EMOJI_OPTIONS = ['📦', '🧲', '⚙️', '🎬', '📸', '🎨', '🎁', '⭐', '💎', '🔧', '📋', '🎯'];
const COLOR_OPTIONS = [
  { value: 'bg-blue-100 text-blue-800', label: 'Bleu' },
  { value: 'bg-purple-100 text-purple-800', label: 'Violet' },
  { value: 'bg-amber-100 text-amber-800', label: 'Ambre' },
  { value: 'bg-green-100 text-green-800', label: 'Vert' },
  { value: 'bg-red-100 text-red-800', label: 'Rouge' },
  { value: 'bg-pink-100 text-pink-800', label: 'Rose' },
  { value: 'bg-indigo-100 text-indigo-800', label: 'Indigo' },
  { value: 'bg-teal-100 text-teal-800', label: 'Teal' },
  { value: 'bg-orange-100 text-orange-800', label: 'Orange' },
  { value: 'bg-stone-100 text-stone-800', label: 'Gris' }
];

const CategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    emoji: '📦',
    color: 'bg-stone-100 text-stone-800',
    order: 999
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/prestation-categories`);
      setCategories(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/prestation-categories/${editingItem.id}`, formData, { withCredentials: true });
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API}/prestation-categories`, formData, { withCredentials: true });
        toast.success('Catégorie créée');
      }
      loadCategories();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Impossible de supprimer une catégorie système');
      return;
    }
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await axios.delete(`${API}/prestation-categories/${id}`, { withCredentials: true });
      toast.success('Catégorie supprimée');
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        emoji: item.emoji || '📦',
        color: item.color || 'bg-stone-100 text-stone-800',
        order: item.order || 999
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      emoji: '📦',
      color: 'bg-stone-100 text-stone-800',
      order: 999
    });
  };

  if (loading) {
    return <div className="p-6">Chargement...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Catégories de Prestations</h1>
          <p className="text-stone-600 mt-1">Créez et gérez vos catégories personnalisées</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="mr-2 h-4 w-4" /> Nouvelle Catégorie
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Modifier la Catégorie' : 'Nouvelle Catégorie'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nom de la catégorie *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Services Premium"
                />
              </div>

              <div>
                <Label>Emoji</Label>
                <div className="grid grid-cols-6 gap-2 mt-2">
                  {EMOJI_OPTIONS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData({ ...formData, emoji })}
                      className={`p-3 text-2xl border-2 rounded-lg transition-all ${
                        formData.emoji === emoji ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Couleur du badge</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {COLOR_OPTIONS.map(colorOpt => (
                    <button
                      key={colorOpt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: colorOpt.value })}
                      className={`p-2 border-2 rounded-lg transition-all ${
                        formData.color === colorOpt.value ? 'border-amber-500' : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <Badge className={colorOpt.value}>{colorOpt.label}</Badge>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 999 })}
                  placeholder="999"
                />
                <p className="text-xs text-stone-500 mt-1">Plus le nombre est petit, plus la catégorie apparaît en premier</p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                  {editingItem ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Ordre</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Aperçu</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>
                    <div className="flex items-center gap-2 text-stone-400">
                      <GripVertical className="h-4 w-4" />
                      <span className="text-sm">{category.order}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{category.name}</TableCell>
                  <TableCell>
                    <Badge className={category.color}>
                      {category.emoji} {category.name}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {category.is_system ? (
                      <Badge className="bg-stone-100 text-stone-600">Système</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">Personnalisée</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openDialog(category)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {!category.is_system && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(category.id, category.is_system)} 
                          className="text-rose-600 hover:text-rose-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">💡 Comment utiliser les catégories</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Les catégories organisent vos prestations dans le questionnaire</li>
          <li>• Les catégories "Système" (Principale, Magnets, Option) ne peuvent pas être supprimées</li>
          <li>• Utilisez l'ordre pour contrôler l'affichage dans le récapitulatif</li>
          <li>• Après création, assignez la catégorie à vos prestations</li>
        </ul>
      </div>
    </div>
  );
};

export default CategoriesManagement;
